import React, { useEffect, useRef, useState } from "react";
import { Alert, AlertTitle, Snackbar } from "@mui/material";
import axios from "axios";
import { useAuthStore } from "../store";
import { formatPln } from "../utils/paymentFormat";
import {
  readPendingDebtPayment,
  clearPendingDebtPayment,
} from "../utils/debtPaymentStorage";

const API = process.env.REACT_APP_BACKEND_ADDRESS;

// PayU's notify webhook books the payment asynchronously (usually seconds
// after the redirect back), so poll a few times before declaring "still
// processing". Delays are cumulative from mount.
const RETRY_DELAYS_MS = [1500, 5000, 12000, 25000];

/**
 * Post-PayU landing confirmation for owed-lesson ("debt") payments.
 *
 * PayU's continueUrl is static — the user lands back with zero context about
 * what they just paid. This reads the stash saved before the redirect
 * (`debtPaymentStorage`), re-fetches /user/me/owed and tells the user exactly
 * which lessons were settled. Crucial when a NEW lesson created fresh debt
 * after the payment: without naming the settled lessons, "you still owe 30 zł"
 * reads as "my payment vanished".
 *
 * Renders nothing when there is no fresh stash or the user is logged out.
 */
const DebtPaymentResultNotice = () => {
  const user = useAuthStore((state) => state.user);
  const [pending, setPending] = useState(() => readPendingDebtPayment());
  const [notice, setNotice] = useState(null); // { severity, title, body }
  const attemptRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!pending || !user) return undefined;

    let cancelled = false;

    const check = async () => {
      try {
        const idToken = await user.getIdToken();
        const resp = await axios.get(`${API}/user/me/owed`, {
          headers: { Authorization: `Bearer ${idToken}` },
        });
        if (cancelled) return;

        const owedIds = new Set(
          [
            ...(resp.data?.online?.lessons || []),
            ...(resp.data?.on_site?.lessons || []),
          ].map((l) => l.lesson_id)
        );
        const settled = pending.lessons.filter(
          (l) => !owedIds.has(l.lesson_id)
        );

        if (settled.length === pending.lessons.length) {
          const total = settled.reduce((sum, l) => sum + (l.price || 0), 0);
          const items = settled
            .map(
              (l) =>
                `${l.lesson_name} (${new Date(l.lesson_date).toLocaleDateString(
                  "pl-PL"
                )})`
            )
            .join(", ");
          setNotice({
            severity: "success",
            title: "Płatność zaksięgowana",
            body: `Opłacono: ${items} — ${formatPln(total)}.`,
          });
          clearPendingDebtPayment();
          setPending(null);
          // Banner/badge still show the old balance — refresh them.
          useAuthStore.getState().fetchPendingSignups();
          return;
        }

        if (attemptRef.current < RETRY_DELAYS_MS.length) {
          timerRef.current = setTimeout(
            check,
            RETRY_DELAYS_MS[attemptRef.current]
          );
          attemptRef.current += 1;
          return;
        }

        // Retries exhausted — payment may still be processing (or was
        // abandoned at PayU). Keep the stash so the next page load
        // re-checks; it expires on its own.
        setNotice({
          severity: "info",
          title: "Płatność w trakcie przetwarzania",
          body: "Jeśli płatność została zrealizowana, status zaktualizuje się za chwilę.",
        });
      } catch {
        // Network hiccup — silently give up this round, stash survives.
      }
    };

    check();
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [pending, user]);

  if (!notice) return null;

  return (
    <Snackbar
      open
      autoHideDuration={notice.severity === "success" ? 12000 : 8000}
      onClose={(event, reason) =>
        reason === "clickaway" ? null : setNotice(null)
      }
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
    >
      <Alert
        severity={notice.severity}
        variant="filled"
        onClose={() => setNotice(null)}
      >
        <AlertTitle>{notice.title}</AlertTitle>
        {notice.body}
      </Alert>
    </Snackbar>
  );
};

export default DebtPaymentResultNotice;
